import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { CotCircleType } from '../../types/community'
import { listCircles } from '../../lib/api/circles'
import { useState } from 'react'
import { usePerson } from '../../contexts/PersonContext'
import { NewCircleModal } from '../circles/NewCircleModal'
import { cn } from '../../lib/cn'
import { SectionLabel } from '../ui/SectionLabel'
import { SidebarAddButton } from './SidebarAddButton'

// Literal ring classes so Tailwind keeps them.
const RING: Record<CotCircleType, string> = {
  Friends: 'border-circle-friends',
  Gaming: 'border-circle-gaming',
  Family: 'border-circle-family',
}

/**
 * "Circles" sidebar section. Consumes GET /community/circles (COTC_Circles fills
 * the stub in); until then it renders no rows. The "+" opens the new-circle modal
 * once COTC_Circles wires it.
 */
export function CirclesSection() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { role } = usePerson()
  const [creating, setCreating] = useState(false)

  const { data: circles = [], isError, isLoading, refetch } = useQuery({
    queryKey: ['community', 'circles'],
    queryFn: listCircles,
    retry: false,
  })

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2 pl-[10px] pr-[6px]">
        <SectionLabel>Circles</SectionLabel>
        {role === 'parent' && <SidebarAddButton title="Start a new circle" onClick={() => setCreating(true)} />}
      </div>
      {isError && <div role="alert" className="px-[10px] text-[12px] text-ink-muted"><p>Circles could not be loaded.</p><button className="mt-2 underline" onClick={() => void refetch()}>Retry circle list</button></div>}
      {isLoading && <p role="status" className="px-[10px] text-[12px] text-ink-muted">Loading circles…</p>}
      {!isError && circles.map((c) => {
        const active = pathname === `/circles/${c.id}`
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => navigate(`/circles/${c.id}`)}
            className={cn(
              'flex w-full items-center gap-[9px] rounded-btn px-[10px] py-[9px] text-left text-[13px] font-medium transition-colors',
              active ? 'bg-paper-deep text-ink' : 'bg-transparent text-ink-soft hover:bg-paper-deep/50',
            )}
          >
            <span className={cn('h-[9px] w-[9px] flex-none rounded-full border-2', RING[c.type])} />
            <span className="min-w-0 flex-1">
              <span className="block truncate">{c.name}</span>
              <span className="block truncate font-mono text-[10px] uppercase tracking-label text-ink-fainter">
                {c.type}
              </span>
            </span>
            <span className="font-mono text-[11px] text-ink-fainter">{c.familyCount}</span>
          </button>
        )
      })}
      {creating && role === 'parent' && <NewCircleModal open onClose={() => setCreating(false)} />}
    </div>
  )
}
